import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';

interface RequestCount {
  count: number;
  timestamp: number;
  window: 'second' | 'minute' | 'hour' | 'day';
}

interface RequestCounters {
  dns: Map<string, RequestCount>;
  api: Map<string, RequestCount>;
}

interface UseRequestCounterOptions {
  enableWarnings?: boolean;
  warningThresholds?: {
    dns?: { perSecond?: number; perMinute?: number; perHour?: number; perDay?: number };
    api?: { perSecond?: number; perMinute?: number; perHour?: number; perDay?: number };
  };
  autoReset?: boolean;
}

export const useRequestCounter = (options: UseRequestCounterOptions = {}) => {
  const {
    enableWarnings = true,
    warningThresholds = {
      dns: { perSecond: 80, perMinute: 800, perHour: 8000, perDay: 40000 },
      api: { perSecond: 8, perMinute: 80, perHour: 800, perDay: 4000 }
    },
    autoReset = true
  } = options;

  const { toast } = useToast();
  const counters = useRef<RequestCounters>({
    dns: new Map(),
    api: new Map()
  });

  const [requestStats, setRequestStats] = useState({
    dns: {
      second: { count: 0, resetTime: Date.now() + 1000 },
      minute: { count: 0, resetTime: Date.now() + 60000 },
      hour: { count: 0, resetTime: Date.now() + 3600000 },
      day: { count: 0, resetTime: Date.now() + 86400000 }
    },
    api: {
      second: { count: 0, resetTime: Date.now() + 1000 },
      minute: { count: 0, resetTime: Date.now() + 60000 },
      hour: { count: 0, resetTime: Date.now() + 3600000 },
      day: { count: 0, resetTime: Date.now() + 86400000 }
    }
  });

  // Window configurations
  const windowConfigs = [
    { key: 'second' as const, duration: 1000 },
    { key: 'minute' as const, duration: 60000 },
    { key: 'hour' as const, duration: 3600000 },
    { key: 'day' as const, duration: 86400000 }
  ];

  // Reset expired counters
  const resetExpiredCounters = useCallback(() => {
    const now = Date.now();
    let hasUpdates = false;

    ['dns', 'api'].forEach((type) => {
      windowConfigs.forEach((window) => {
        const key = `${type}:${window.key}`;
        const counter = counters.current[type as keyof RequestCounters].get(key);
        
        if (counter && now >= counter.timestamp + window.duration) {
          counters.current[type as keyof RequestCounters].set(key, {
            count: 0,
            timestamp: Math.floor(now / window.duration) * window.duration,
            window: window.key
          });
          hasUpdates = true;
        }
      });
    });

    if (hasUpdates) {
      updateRequestStats();
    }
  }, []);

  // Update the state from counters
  const updateRequestStats = useCallback(() => {
    const newStats = {
      dns: {} as any,
      api: {} as any
    };

    ['dns', 'api'].forEach((type) => {
      windowConfigs.forEach((window) => {
        const key = `${type}:${window.key}`;
        const counter = counters.current[type as keyof RequestCounters].get(key);
        
        newStats[type as keyof typeof newStats][window.key] = {
          count: counter?.count || 0,
          resetTime: counter ? counter.timestamp + window.duration : Date.now() + window.duration
        };
      });
    });

    setRequestStats(newStats);
  }, []);

  // Initialize counters
  useEffect(() => {
    const now = Date.now();
    
    ['dns', 'api'].forEach((type) => {
      windowConfigs.forEach((window) => {
        const key = `${type}:${window.key}`;
        const windowStart = Math.floor(now / window.duration) * window.duration;
        
        counters.current[type as keyof RequestCounters].set(key, {
          count: 0,
          timestamp: windowStart,
          window: window.key
        });
      });
    });

    updateRequestStats();
  }, [updateRequestStats]);

  // Auto-reset timer
  useEffect(() => {
    if (!autoReset) return;

    const interval = setInterval(resetExpiredCounters, 1000);
    return () => clearInterval(interval);
  }, [autoReset, resetExpiredCounters]);

  // Increment counter function
  const incrementCounter = useCallback((type: 'dns' | 'api', amount: number = 1) => {
    const now = Date.now();
    let shouldShowWarning = false;

    windowConfigs.forEach((window) => {
      const key = `${type}:${window.key}`;
      const windowStart = Math.floor(now / window.duration) * window.duration;
      const counter = counters.current[type].get(key);

      if (!counter || counter.timestamp < windowStart) {
        // New window, reset counter
        counters.current[type].set(key, {
          count: amount,
          timestamp: windowStart,
          window: window.key
        });
      } else {
        // Increment existing counter
        counter.count += amount;
      }

      // Check for warnings
      if (enableWarnings && warningThresholds[type]?.[`per${window.key.charAt(0).toUpperCase() + window.key.slice(1)}` as keyof typeof warningThresholds[typeof type]]) {
        const threshold = warningThresholds[type]?.[`per${window.key.charAt(0).toUpperCase() + window.key.slice(1)}` as keyof typeof warningThresholds[typeof type]];
        const currentCount = counters.current[type].get(key)?.count || 0;
        
        if (threshold && currentCount >= threshold * 0.9 && !shouldShowWarning) {
          shouldShowWarning = true;
          
          // Show warning toast
          toast({
            title: `${type.toUpperCase()} Rate Limit Warning`,
            description: `Approaching ${type} rate limit: ${currentCount}/${threshold} requests per ${window.key}`,
            variant: currentCount >= threshold ? "destructive" : "default",
          });
        }
      }
    });

    updateRequestStats();
    return shouldShowWarning;
  }, [enableWarnings, warningThresholds, toast, updateRequestStats]);

  // Track DNS request
  const trackDNSRequest = useCallback((count: number = 1) => {
    return incrementCounter('dns', count);
  }, [incrementCounter]);

  // Track API request
  const trackAPIRequest = useCallback((count: number = 1) => {
    return incrementCounter('api', count);
  }, [incrementCounter]);

  // Get usage percentage for a specific window
  const getUsagePercentage = useCallback((type: 'dns' | 'api', window: 'second' | 'minute' | 'hour' | 'day') => {
    const current = requestStats[type][window].count;
    const threshold = warningThresholds[type]?.[`per${window.charAt(0).toUpperCase() + window.slice(1)}` as keyof typeof warningThresholds[typeof type]] || 100;
    return Math.min(100, (current / threshold) * 100);
  }, [requestStats, warningThresholds]);

  // Get time remaining until reset for a specific window
  const getTimeUntilReset = useCallback((type: 'dns' | 'api', window: 'second' | 'minute' | 'hour' | 'day') => {
    const resetTime = requestStats[type][window].resetTime;
    const now = Date.now();
    return Math.max(0, resetTime - now);
  }, [requestStats]);

  // Reset all counters manually
  const resetCounters = useCallback((type?: 'dns' | 'api') => {
    const now = Date.now();
    const typesToReset = type ? [type] : ['dns', 'api'];

    typesToReset.forEach((t) => {
      windowConfigs.forEach((window) => {
        const key = `${t}:${window.key}`;
        const windowStart = Math.floor(now / window.duration) * window.duration;
        
        counters.current[t as keyof RequestCounters].set(key, {
          count: 0,
          timestamp: windowStart,
          window: window.key
        });
      });
    });

    updateRequestStats();
  }, [updateRequestStats]);

  // Get current stats summary
  const getStatsSummary = useCallback(() => {
    const summary = {
      dns: {
        total: Object.values(requestStats.dns).reduce((sum, stat) => sum + stat.count, 0),
        highest: Math.max(...Object.values(requestStats.dns).map(stat => stat.count)),
        windows: requestStats.dns
      },
      api: {
        total: Object.values(requestStats.api).reduce((sum, stat) => sum + stat.count, 0),
        highest: Math.max(...Object.values(requestStats.api).map(stat => stat.count)),
        windows: requestStats.api
      }
    };

    return summary;
  }, [requestStats]);

  return {
    requestStats,
    trackDNSRequest,
    trackAPIRequest,
    getUsagePercentage,
    getTimeUntilReset,
    resetCounters,
    getStatsSummary
  };
};